import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StorefrontAPI from 'utilities/storefrontApi';
import CustomerTickets from './CustomerTickets';
import { saveSession, loadSession, clearSession } from 'utilities/storefrontSession';
import {
  wrap, cardStyle, inputStyle, primaryBtn,
  navigateToLogin,
} from './styles';
import useBrandFromParams from './useBrandFromParams';
import BrandHeader from './BrandHeader';
import Centered from './Centered';

const STATUS_BG = {
  pending: '#fef3c7', paid: '#dcfce7',
  fulfilled: '#dbeafe', cancelled: '#e5e7eb', refunded: '#fee2e2',
};
const STATUS_FG = {
  pending: '#92400e', paid: '#166534',
  fulfilled: '#1e40af', cancelled: '#374151', refunded: '#991b1b',
};

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

/**
 * Signed-in customer account page: editable profile + recent orders
 * (with self-cancel for pending ones) + sign-out.
 *
 * Requires a saved storefront session for the current brand. If the
 * session is missing or stale (auth fails), redirects to /login.
 */
export default function StorefrontAccount() {
  const { brand, error: brandError } = useBrandFromParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const accentColor = brand?.brand_color || '#422AFB';

  useEffect(() => {
    if (!brand) return;
    const session = loadSession(brand._id);
    if (!session?.token) {
      navigateToLogin(navigate, brand);
      return;
    }
    setLoading(true);
    StorefrontAPI.me(brand._id)
      .then((me) => { setCustomer(me); setEdit(me); })
      .catch(() => {
        clearSession(brand._id);
        navigateToLogin(navigate, brand);
      })
      .finally(() => setLoading(false));
    StorefrontAPI.listMyOrders(brand._id, 25)
      .then((rows) => setOrders(Array.isArray(rows) ? rows : []))
      .catch(() => { /* leave empty */ });
  }, [brand, navigate]);

  const save = async () => {
    if (!brand || !edit) return;
    setSaving(true);
    try {
      const updated = await StorefrontAPI.updateMe(brand._id, {
        display_name: edit.display_name,
        contact_email: edit.contact_email,
        contact_phone: edit.contact_phone,
        avatar_url: edit.avatar_url,
        marketing_email_opt_in: !!edit.marketing_email_opt_in,
      });
      setCustomer(updated);
      saveSession(brand._id, { ...(loadSession(brand._id) || {}), customer: updated });
    } catch (err) {
      alert(err?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    if (!brand) return;
    try { await StorefrontAPI.logout(brand._id); } catch (_) { /* best-effort */ }
    clearSession(brand._id);
    navigate(brand?._id ? `/site/${brand._id}` : '/');
  };

  // Customer self-cancel for a pending order. Confirms inline (window.confirm
  // + window.prompt) so we don't ship a modal library just for one prompt.
  const cancelMine = async (order) => {
    if (!brand || !order?._id) return;
    if (!window.confirm(`Cancel order ${order.number || ''}? This can't be undone.`)) return;
    const reason = (window.prompt('Reason (optional)', '') || '').trim();
    try {
      await StorefrontAPI.cancelMyOrder(brand._id, order._id, reason);
      const fresh = await StorefrontAPI.listMyOrders(brand._id, 25);
      setOrders(Array.isArray(fresh) ? fresh : []);
    } catch (err) {
      alert(err?.message || 'Could not cancel');
    }
  };

  if (brandError) return <Centered>{brandError}</Centered>;
  if (!brand) return <Centered>Loading…</Centered>;
  if (loading) return <Centered>Loading your account…</Centered>;
  if (!customer || !edit) return <Centered>Couldn't load your profile.</Centered>;

  const initial = (edit.display_name || edit.contact_email || '?').slice(0, 1).toUpperCase();

  return (
    <div style={{ ...wrap, alignItems: 'flex-start', paddingTop: 60 }}>
      <div style={{ ...cardStyle, maxWidth: 560 }}>
        <BrandHeader brand={brand} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
          {edit.avatar_url ? (
            <img src={edit.avatar_url} alt='' style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: accentColor, color: '#fff',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, fontWeight: 700,
            }}>{initial}</div>
          )}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{customer.display_name || 'Your account'}</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>{customer.contact_email}</div>
          </div>
        </div>

        <Section label='Display name'>
          <input style={inputStyle} value={edit.display_name || ''} onChange={(e) => setEdit({ ...edit, display_name: e.target.value })} />
        </Section>
        <Section label='Avatar URL'>
          <input style={inputStyle} value={edit.avatar_url || ''} onChange={(e) => setEdit({ ...edit, avatar_url: e.target.value })} placeholder='https://…' />
        </Section>
        <Section label='Contact email'>
          <input style={inputStyle} type='email' value={edit.contact_email || ''} onChange={(e) => setEdit({ ...edit, contact_email: e.target.value })} />
        </Section>
        <Section label='Phone'>
          <input style={inputStyle} value={edit.contact_phone || ''} onChange={(e) => setEdit({ ...edit, contact_phone: e.target.value })} placeholder='+1…' />
        </Section>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', margin: '6px 0 18px' }}>
          <input type='checkbox' checked={!!edit.marketing_email_opt_in} onChange={(e) => setEdit({ ...edit, marketing_email_opt_in: e.target.checked })} />
          Email me updates and offers from {brand.name}.
        </label>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={save} style={primaryBtn(accentColor)} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button onClick={logout} style={{ ...primaryBtn('#fff'), color: '#0f172a', border: '1px solid #d1d5db' }}>
            Sign out
          </button>
        </div>

        <div style={{ marginTop: 24, fontSize: 12, color: '#94a3b8' }}>
          You're signed in to <strong>{brand.name}</strong>. Your activity here is separate from any other brand you may use.
        </div>

        <div style={{ marginTop: 28, borderTop: '1px solid #e5e7eb', paddingTop: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
            Your orders
          </div>
          {orders.length === 0 && (
            <div style={{ fontSize: 13, color: '#64748b' }}>
              No orders yet. Once you place one, it'll show up here.
            </div>
          )}
          {orders.map((o) => (
            <div
              key={o._id}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 0', borderBottom: '1px solid #f1f5f9',
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                  {o.number || 'Order'}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {o.placed_at ? new Date(o.placed_at).toLocaleDateString() : ''} · {(o.line_items || []).length} item(s)
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {new Intl.NumberFormat(undefined, { style: 'currency', currency: o.currency || 'USD' })
                    .format(Number(o.grand_total || 0))}
                </div>
                <div style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                  fontSize: 11, fontWeight: 600,
                  background: STATUS_BG[o.status] || '#e5e7eb',
                  color:      STATUS_FG[o.status] || '#374151',
                }}>
                  {o.status}
                </div>
                {o.status === 'pending' && (
                  <button
                    type='button'
                    onClick={() => cancelMine(o)}
                    style={{
                      display: 'block', marginTop: 6,
                      background: 'transparent', border: 'none',
                      color: '#dc2626', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', padding: 0,
                    }}
                  >
                    Cancel order
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Support lives beside the orders rather than behind a link: the
            question somebody has is almost always about one of the rows
            directly above it. */}
        <CustomerTickets brand={brand} accent={accentColor} />
      </div>
    </div>
  );
}
